import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CKEditorComponent from '../../components/CKEditorComponent';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import privacyPolicyApi from '../../api/privacyPolicyApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    content: '',
  });
  const [recordExists, setRecordExists] = useState(false);

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      setLoading(true);
      try {
        const response = await privacyPolicyApi.getPrivacyPolicy();
        if (response.success && response.data) {
          setFormData({ content: response.data.content });
          setRecordExists(true);
        } else {
          setFormData({ content: '' });
          setRecordExists(false);
        }
      } catch (err) {
        console.error('Failed to fetch Privacy Policy:', err);
        addToast(err.response?.data?.message || 'Failed to load Privacy Policy. Please try again.', 'error');
        setRecordExists(false);
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacyPolicy();
  }, [addToast]);

  const handleContentChange = (content) => {
    setFormData({ ...formData, content: content });
    if (validationErrors.content) {
      setValidationErrors(prev => ({ ...prev, content: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setValidationErrors({});

    try {
      if (recordExists) {
        await privacyPolicyApi.updatePrivacyPolicy(formData);
        addToast('Privacy Policy updated successfully!', 'success');
      } else {
        await privacyPolicyApi.createPrivacyPolicy(formData);
        addToast('Privacy Policy created successfully!', 'success');
        setRecordExists(true);
      }
    } catch (err) {
      console.error('Failed to save Privacy Policy:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to save Privacy Policy. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">{recordExists ? 'Edit Privacy Policy' : 'Create Privacy Policy'}</h1>
      <form onSubmit={handleSubmit}>
        <CKEditorComponent
          label="Content"
          value={formData.content}
          onChange={handleContentChange}
          required={true}
          error={validationErrors?.content?.[0]}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            {recordExists ? 'Update Privacy Policy' : 'Create Privacy Policy'}
          </Button>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PrivacyPolicy;